import { AnimatePresence, motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { X } from 'react-feather'
import { Button } from 'reactstrap'
import ProfilePic from 'components/PMBProfilePic'
import PMBAsyncSelectPaginate from 'components/PMBAsyncSelectPaginate'
import { useLazyFetchProjectShareContactsQuery } from './projectsMenuListApiSlice'

// Share APIs need an account id; fetchProjectShareContacts leaves relationUserId null for contacts without one.
// Fallback order: relationUserId ?? relationEmailUserId ?? userId ?? null.
export const getContactUserId = (contact) =>
  contact?.relationUserId ??
  contact?.relationEmailUserId ??
  contact?.userId ??
  null

export const getContactKey = (contact) =>
  contact?.contactUniqueKey ||
  (getContactUserId(contact) ? `user-${getContactUserId(contact)}` : null) ||
  (contact?.id ? `contact-${contact.id}` : null) ||
  contact?.relationPhoneNumber ||
  contact?.relationEmail ||
  contact?.name

export const buildParticipantFromContact = (contact) => ({
  key: getContactKey(contact),
  userId: getContactUserId(contact),
  name:
    contact?.relationName ||
    contact?.fullName ||
    contact?.name ||
    '',
  email:
    contact?.relationEmail ||
    contact?.relationPhoneNumber ||
    contact?.email ||
    contact?.phoneNumber ||
    '',
  profileImg: contact?.profile_img || contact?.profileImg || null,
})

const PAGE_SIZE = 10

// "Cari Orang" search (team, farmers, address book) plus the "Bagikan ke" list, shared by the share modals.
// The parent owns `participants`; renderActions adds per-person controls such as a role select.
const ShareContactPicker = ({
  isOpen,
  participants,
  onSelectContact,
  onRemove,
  renderActions,
  inputRef,
  closeOnSelect = false,
}) => {
  const [fetchContacts] = useLazyFetchProjectShareContactsQuery()

  const { profile, ownerData } = useSelector((s) => s?.dashboard || {})
  const currentUserId =
    profile?.userId ??
    ownerData?.userId ??
    (() => {
      try {
        const u =
          JSON.parse(localStorage.getItem('userData')) ||
          JSON.parse(localStorage.getItem('_user'))
        return u?.userId ?? u?.id ?? null
      } catch {
        return null
      }
    })()

  const isAlreadySelected = useCallback(
    (contact) => {
      const contactUserId = getContactUserId(contact)
      const contactKey = getContactKey(contact)
      return participants.some((participant) => {
        if (contactUserId && participant.userId) {
          return String(participant.userId) === String(contactUserId)
        }
        return Boolean(
          contactKey && participant.key && participant.key === contactKey,
        )
      })
    },
    [participants],
  )

  const loadOptions = useCallback(
    async (searchQuery, _prevOptions, { page = 0 } = {}) => {
      if (!isOpen) {
        return {
          options: [],
          hasMore: false,
          additional: { page: 0 },
        }
      }

      const searchTerm = (searchQuery || '').trim()

      if (searchTerm.length > 0 && searchTerm.length < 3) {
        return {
          options: [],
          hasMore: false,
          additional: { page },
        }
      }

      try {
        const response = await fetchContacts({
          s: searchTerm,
          page,
          size: PAGE_SIZE,
        }).unwrap()

        const items = response?.items || []

        const availableItems = items.filter((contact) => {
          const contactUserId = getContactUserId(contact)
          if (
            currentUserId &&
            contactUserId &&
            String(contactUserId) === String(currentUserId)
          ) {
            return false
          }
          return Boolean(contact.relationPhoneNumber || contact.relationEmail)
        })

        const options = availableItems.map((contact) => ({
          value:
            getContactKey(contact) ||
            getContactUserId(contact) ||
            contact.id,
          label:
            contact.relationName ||
            contact.fullName ||
            contact.name ||
            '',
          contact,
        }))

        const totalPages =
          response?.totalPages ??
          (items.length === PAGE_SIZE ? page + 2 : page + 1)
        const hasMore =
          response?.hasMore ??
          (page + 1 < totalPages && items.length >= PAGE_SIZE)

        return {
          options,
          hasMore,
          additional: {
            page: page + 1,
          },
        }
      } catch {
        return {
          options: [],
          hasMore: false,
          additional: { page },
        }
      }
    },
    [isOpen, currentUserId, fetchContacts],
  )

  const handleSelectOption = (selectedOption) => {
    if (!selectedOption?.contact) return
    const contact = selectedOption.contact
    if (!getContactUserId(contact)) return
    if (isAlreadySelected(contact)) return
    onSelectContact(contact)
  }

  const isOptionDisabled = (option) => {
    const contact = option?.contact
    if (!contact) return false
    return !getContactUserId(contact) || isAlreadySelected(contact)
  }

  const formatOptionLabel = (option) => {
    const contact = option?.contact
    const hasAccount = Boolean(getContactUserId(contact))
    const isSelected = isAlreadySelected(contact)

    return (
      <div className="d-flex flex-column py-1">
        <span className={!hasAccount || isSelected ? 'text-muted' : ''}>
          {contact?.relationName || contact?.fullName || contact?.name || option?.label}
        </span>
        <span className="text-muted small">
          {contact?.relationPhoneNumber || contact?.relationEmail}
          {!hasAccount && ' · Belum punya akun'}
          {isSelected && ' · Sudah dipilih'}
        </span>
      </div>
    )
  }

  return (
    <>
      <div className="position-relative w-100">
        <label className="form-label">Cari Orang</label>
        <PMBAsyncSelectPaginate
          key={`share-contact-picker-${isOpen ? 'open' : 'closed'}`}
          selectRef={inputRef}
          value={null}
          loadOptions={loadOptions}
          onChange={handleSelectOption}
          placeholder="Cari nama atau nomor handphone"
          isClearable={false}
          additional={{ page: 0 }}
          debounceTimeout={300}
          cacheUniqs={[isOpen, participants.length]}
          isOptionDisabled={isOptionDisabled}
          formatOptionLabel={formatOptionLabel}
          closeMenuOnSelect={closeOnSelect}
          noOptionsMessage={({ inputValue }) => {
            const query = (inputValue || '').trim()
            if (query.length > 0 && query.length < 3) {
              return 'Ketik minimal 3 karakter untuk mencari'
            }
            return 'Pencarian tidak ditemukan'
          }}
          loadingMessage={() => 'Memuat...'}
        />
      </div>

      <motion.div
        layout
        className={participants.length > 0 ? 'mt-4' : ''}
        transition={{ layout: { duration: 0.22, ease: 'easeOut' } }}
      >
        {participants.length > 0 && <label className="fw-semibold mb-2">Bagikan ke:</label>}

        <div className="d-flex flex-column gap-2">
          <AnimatePresence initial={false}>
            {participants.map((p) => (
              <motion.div
                key={p.key || p.userId}
                layout
                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                className="d-flex align-items-center gap-2 border rounded p-2"
              >
                <ProfilePic
                  imgSrc={p.profileImg}
                  altText={p.name}
                  name={p.name}
                  avatarSeed={p.userId}
                  width={36}
                  height={35}
                />

                <div className="flex-grow-1">
                  <div className="fw-semibold text-sm">{p.name}</div>
                  {p.email && (
                    <div className="text-muted" style={{ fontSize: '12px' }}>
                      {p.email}
                    </div>
                  )}
                </div>

                {renderActions?.(p)}

                <Button color="link" className="text-danger p-1" onClick={() => onRemove(p.key || p.userId)}>
                  <X size={18} />
                </Button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  )
}

ShareContactPicker.propTypes = {
  isOpen: PropTypes.bool,
  participants: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelectContact: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  renderActions: PropTypes.func,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  closeOnSelect: PropTypes.bool,
}

export default ShareContactPicker
